import { useEffect, useMemo, useRef, useState } from 'react';
import './Pager.css';

const fmt = (n) => Number(n || 0).toLocaleString();

function readSize(storageKey, fallback, sizes) {
  if (!storageKey) return fallback;
  try {
    const saved = Number(localStorage.getItem(`pageSize:${storageKey}`));
    return sizes.includes(saved) ? saved : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Pages a list on the client.
 *   const paged = usePaged(rows, { pageSize: 50, resetKey: `${filter}|${search}`, storageKey: 'locations' });
 *   paged.items -> rows of the current page;  <Pager paged={paged} />
 * The page goes back to 1 when resetKey changes, and stays in range when
 * the list shrinks (live refreshes). storageKey remembers the page size.
 */
export function usePaged(list, { pageSize = 50, sizes = [25, 50, 100, 200], resetKey = '', storageKey } = {}) {
  const allSizes = useMemo(() => (sizes.includes(pageSize) ? sizes : [...sizes, pageSize].sort((a, b) => a - b)), [sizes, pageSize]);
  const [size, setSizeState] = useState(() => readSize(storageKey, pageSize, allSizes));
  const [page, setPage] = useState(1);
  const anchorRef = useRef(null);
  const rows = list || [];
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(page, pages);

  useEffect(() => setPage(1), [resetKey]);

  const setSize = (value) => {
    setSizeState(value);
    setPage(1);
    if (storageKey) {
      try {
        localStorage.setItem(`pageSize:${storageKey}`, String(value));
      } catch {
        // storage unavailable
      }
    }
  };

  const go = (next) => {
    setPage(Math.min(Math.max(1, next), pages));
    // Back to the top of the list when it has scrolled out of view.
    const el = anchorRef.current;
    if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const items = useMemo(() => rows.slice((current - 1) * size, current * size), [rows, current, size]);

  return { items, page: current, pages, total, size, sizes: allSizes, setSize, go, anchorRef };
}

/** Page numbers: first, last, current ±1, gaps as '…'. */
function pageList(current, total) {
  const set = new Set([1, total, current - 1, current, current + 1]);
  const list = [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  list.forEach((n, i) => {
    if (i && n - list[i - 1] > 1) out.push(`gap-${n}`);
    out.push(n);
  });
  return out;
}

/**
 * "1–50 of 1,234 · ‹ 1 2 3 … 25 › · Per page [50]". Hidden when everything
 * fits on one page at the smallest size. noun: "locations", "batches"…
 */
export function Pager({ paged, noun = 'rows', compact = false, className = '' }) {
  const { page, pages, total, size, sizes, setSize, go } = paged;
  if (total <= Math.min(...sizes) && pages === 1) return null;
  const from = total ? (page - 1) * size + 1 : 0;
  const to = Math.min(page * size, total);
  return (
    <nav className={`pager${compact ? ' pager--compact' : ''} ${className}`.trim()} aria-label={`${noun} pages`}>
      <span className="pager__range">
        <b>
          {fmt(from)}–{fmt(to)}
        </b>{' '}
        of {fmt(total)} {noun}
      </span>
      <div className="pager__pages">
        <button type="button" onClick={() => go(page - 1)} disabled={page <= 1} aria-label="Previous page">
          ‹
        </button>
        {pageList(page, pages).map((item) =>
          typeof item === 'number' ? (
            <button
              key={item}
              type="button"
              className={item === page ? 'is-current' : ''}
              aria-current={item === page ? 'page' : undefined}
              onClick={() => go(item)}
            >
              {fmt(item)}
            </button>
          ) : (
            <span key={item} className="pager__gap">
              …
            </span>
          ),
        )}
        <button type="button" onClick={() => go(page + 1)} disabled={page >= pages} aria-label="Next page">
          ›
        </button>
      </div>
      <label className="pager__size">
        Per page
        <select value={size} onChange={(e) => setSize(Number(e.target.value))}>
          {sizes.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
    </nav>
  );
}

export default Pager;
